import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader, CheckCircle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { useUserStore } from '../store/userStore';
import { ordersApi } from '../services/ordersApi';
import { hallApi, type HallPaymentStatus } from '../services/hallApi';
import { myOrdersQueryKey } from '../queries/myOrders';
import { appPath } from '../routes/appPaths';
import { DineInThemeShell } from '../components/DineInThemeShell';
import { HallPaymentSuccess } from '../components/HallPaymentSuccess';
import { useDineInMenuTheme } from '../hooks/useDineInMenuTheme';

const STORAGE_KEY = 'theory_pending_payment_order_id';

export default function PaymentReturnScreen() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const hallPid = params.get('hall');
  const { theme, tokens, fadeMs } = useDineInMenuTheme();
  const queryClient = useQueryClient();
  const token = useUserStore(s => s.token);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualHint, setManualHint] = useState<string | null>(null);
  const [hallPayment, setHallPayment] = useState<HallPaymentStatus | null>(null);
  const [hallBack, setHallBack] = useState('/menu');

  useEffect(() => {
    if (!hallPid) return;
    let cancelled = false;
    let attempts = 0;
    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > 60) {
        setError('Оплата не подтвердилась.');
        return;
      }
      try {
        let row = await hallApi.getPayment(hallPid);
        if (row.tableId) setHallBack(`/menu?table=${encodeURIComponent(row.tableId)}`);
        if (row.status === 'paid' && row.iikoOrderId && !(row.items && row.items.length)) {
          try {
            const table = await hallApi.getOrder(row.iikoOrderId);
            row = {
              ...row,
              items: table.order?.items ?? [],
              waiterName: table.order?.waiterName ?? row.waiterName,
            };
          } catch {
            /* состав из iiko опционален */
          }
        }
        setHallPayment(row);
        if (row.status === 'paid') {
          setDone(true);
          return;
        }
        if (row.status === 'failed') {
          setError('Оплата не прошла.');
          return;
        }
      } catch {
        /* retry */
      }
      setTimeout(tick, 2000);
    };
    tick();
    return () => {
      cancelled = true;
    };
  }, [hallPid]);

  useEffect(() => {
    if (hallPid) return;
    const pendingId = sessionStorage.getItem(STORAGE_KEY);
    if (!pendingId) {
      navigate(appPath('/home'), { replace: true });
      return;
    }
    if (!token) {
      setError('Войдите в аккаунт');
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 90;

    const tick = async () => {
      if (cancelled) return;
      attempts += 1;
      if (attempts > maxAttempts) {
        setError('Оплата не подтвердилась. Проверьте заказы в профиле.');
        sessionStorage.removeItem(STORAGE_KEY);
        return;
      }
      try {
        const list = await ordersApi.getMyOrders(token);
        const row = list.find(o => o.id === pendingId);
        if (row?.paymentStatus === 'paid' && row.iikoOrderId) {
          sessionStorage.removeItem(STORAGE_KEY);
          await queryClient.invalidateQueries({ queryKey: myOrdersQueryKey(token) });
          setDone(true);
          setTimeout(() => navigate(appPath('/home'), { replace: true }), 2000);
          return;
        }
      } catch {
        /* retry */
      }
      setTimeout(tick, 2000);
    };

    tick();
    return () => {
      cancelled = true;
    };
  }, [hallPid, token, navigate, queryClient]);

  const tryResolvePaid = async (
    pendingId: string,
    list: Awaited<ReturnType<typeof ordersApi.getMyOrders>>,
  ) => {
    const row = list.find(o => o.id === pendingId);
    if (row?.paymentStatus === 'paid' && row.iikoOrderId) {
      sessionStorage.removeItem(STORAGE_KEY);
      if (token) {
        await queryClient.invalidateQueries({ queryKey: myOrdersQueryKey(token) });
      }
      setDone(true);
      setTimeout(() => navigate(appPath('/home'), { replace: true }), 2000);
      return true;
    }
    return false;
  };

  const handleManualCheck = async () => {
    const pendingId = sessionStorage.getItem(STORAGE_KEY);
    if (!pendingId || !token) return;
    setManualLoading(true);
    setManualHint(null);
    try {
      await ordersApi.syncMyPayment(token, pendingId);
      const list = await ordersApi.getMyOrders(token);
      if (!(await tryResolvePaid(pendingId, list))) {
        setManualHint('В ЮKassa оплата ещё не подтверждена. Попробуйте снова после оплаты.');
      }
    } catch (e) {
      setManualHint(e instanceof Error ? e.message : 'Не удалось проверить оплату');
    } finally {
      setManualLoading(false);
    }
  };

  if (done && hallPid && hallPayment) {
    return (
      <DineInThemeShell theme={theme} fadeMs={fadeMs}>
        <HallPaymentSuccess payment={hallPayment} tokens={tokens} />
      </DineInThemeShell>
    );
  }

  if (done) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center" style={{ background: '#0a0a0a' }}>
        <motion.div initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} className="text-center px-8">
          <CheckCircle size={72} className="mx-auto mb-4" style={{ color: '#fff' }} strokeWidth={1.5} />
          <h2 className="text-2xl font-bold text-white mb-2">Оплата прошла</h2>
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
            {hallPid
              ? hallPayment?.kind === 'tip'
                ? 'Чаевые оплачены.'
                : ''
              : 'Заказ передан на кухню'}
          </p>
          {hallPid ? (
            <Link to={hallBack} className="inline-block mt-6 text-sm underline text-white/70">
              Вернуться в меню
            </Link>
          ) : null}
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-6" style={{ background: '#0a0a0a' }}>
      {error ? (
        <div className="text-center max-w-sm">
          <p className="text-sm font-medium mb-6" style={{ color: '#f87171' }}>{error}</p>
            {hallPid ? (
              <Link to={hallBack} className="inline-block text-sm font-semibold text-white underline">
                В меню
              </Link>
            ) : (
              <button
                type="button"
                onClick={() => navigate(appPath('/orders'), { replace: true })}
                className="text-sm font-semibold text-white underline"
              >
                К заказам
              </button>
            )}
        </div>
      ) : (
        <div className="text-center max-w-sm">
          <Loader size={40} className="mx-auto mb-4 animate-spin text-white/80" />
          <p className="text-base font-semibold text-white">Подтверждаем оплату…</p>
          <p className="text-xs mt-2" style={{ color: 'rgba(255,255,255,0.45)' }}>
            Обычно это занимает несколько секунд
          </p>
          {hallPid ? null : (
            <>
          <button
            type="button"
            disabled={manualLoading}
            onClick={handleManualCheck}
            className="mt-8 w-full rounded-xl py-3 text-sm font-semibold text-white border border-white/20 disabled:opacity-50"
          >
            {manualLoading ? 'Проверяем…' : 'Проверить оплату'}
          </button>
          <p className="text-[11px] mt-3 leading-relaxed" style={{ color: 'rgba(255,255,255,0.35)' }}>
            Обычно проверка происходит автомтически 
          </p>
            </>
          )}
          {manualHint ? (
            <p className="text-xs mt-4" style={{ color: '#fbbf24' }}>
              {manualHint}
            </p>
          ) : null}
        </div>
      )}
    </div>
  );
}

export { STORAGE_KEY };
